import React from 'react';
import { motion } from 'framer-motion';
import { AlertTriangle, Droplets, Flame, Activity, Database, Train } from 'lucide-react';

// Import images
import povertyImg from '../../assets/images/crisis/poverty.jpg';
import waterImg from '../../assets/images/crisis/water.jpg';
import securityImg from '../../assets/images/crisis/security.jpg';
import industryImg from '../../assets/images/crisis/industry.jpg';
import deficitImg from '../../assets/images/crisis/deficit.jpg';
import mobilityImg from '../../assets/images/crisis/mobility.png';

const BentoItem = ({ title, value, subtitle, icon: Icon, color, image, size = "small", delay = 0, source, sourceUrl }) => {
    const isLarge = size === "large";

    return (
        <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: delay * 0.1 }}
            className={`${isLarge ? 'md:col-span-2 md:row-span-2' : ''} bg-surface border border-zinc-800 p-6 rounded-xl hover:border-primary/50 transition-colors group relative overflow-hidden`}
        >
            {image && (
                <>
                    <div className="absolute inset-0 bg-cover bg-center transition-transform duration-700 group-hover:scale-110" style={{ backgroundImage: `url(${image})` }}></div>
                    <div className="absolute inset-0 bg-black/80 group-hover:bg-black/70 transition-colors"></div>
                </>
            )}

            <div className="absolute top-4 right-4 p-2 bg-background/80 backdrop-blur rounded-full border border-zinc-800 group-hover:bg-zinc-800 transition-colors z-10">
                <Icon size={20} className={color} />
            </div>

            <div className="flex flex-col h-full justify-between mt-4 relative z-10">
                <div>
                    <h3 className="text-zinc-400 font-mono text-xs uppercase tracking-wider mb-2">{title}</h3>
                    <p className={`font-mono font-bold text-white ${isLarge ? 'text-5xl' : 'text-3xl'}`}>
                        {value}
                    </p>
                </div>

                <div className="mt-4">
                    <p className="text-sm text-zinc-300 leading-relaxed mb-3">
                        {subtitle}
                    </p>
                    {source && (
                        <a
                            href={sourceUrl || "#"}
                            target="_blank"
                            rel="noreferrer"
                            className="inline-flex items-center gap-1 text-[10px] text-zinc-500 font-mono hover:text-primary transition-colors border-b border-dashed border-zinc-700 hover:border-primary"
                        >
                            [Fuente: {source}]
                        </a>
                    )}
                </div>
            </div>

            {/* Background decoration if no image */}
            {!image && (
                <div className={`absolute -bottom-10 -right-10 w-32 h-32 bg-${color.replace('text-', '')}/5 rounded-full blur-3xl pointer-events-none`}></div>
            )}
        </motion.div>
    );
};

const CrisisGrid = () => {
    return (
        <section className="py-24 bg-background relative">
            <div className="container mx-auto px-6">
                <div className="mb-16 text-center max-w-3xl mx-auto">
                    <span className="text-primary font-mono text-sm tracking-widest uppercase mb-4 block">SCANNING SECTOR 08</span>
                    <h2 className="text-4xl md:text-5xl font-display font-bold text-white mb-6">Diagnóstico Crítico</h2>
                    <p className="text-text-muted text-lg">
                        Más allá de la percepción, los <strong className='text-white'>números duros</strong> revelan un sistema operativo urbano al límite de su capacidad.
                    </p>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-3 gap-4 auto-rows-[200px]">
                    {/* Poverty - Large Block */}
                    <BentoItem
                        size="large"
                        title="Pobreza Estructural"
                        value="151,224"
                        subtitle="Personas viviendo en situación de pobreza (40.1%) a pesar de tener el menor índice de rezago social teórico del país."
                        icon={Activity}
                        color="text-red-500"
                        image={povertyImg}
                        delay={0}
                        source="CONEVAL, Medición de Pobreza Municipal 2020"
                        sourceUrl="https://www.coneval.org.mx/Medicion/Paginas/Pobreza_municipal.aspx"
                    />

                    {/* Water */}
                    <BentoItem
                        title="Colapso Hídrico"
                        value="$3,800"
                        subtitle="Costo mensual promedio en pipas privadas para familias en zonas de tandeo."
                        icon={Droplets}
                        color="text-blue-400"
                        image={waterImg}
                        delay={1}
                        source="Encuesta Vecinal i³ (Muestra: 1,200 hogares)"
                        sourceUrl="/iztacalco"
                    />

                    {/* Security - Updated to Social Tension */}
                    <BentoItem
                        title="Tensión Social"
                        value="56.4%"
                        subtitle="La conflictividad vecinal y el estrés urbano superan a la percepción criminal criminal delictiva."
                        icon={AlertTriangle}
                        color="text-yellow-400"
                        image={securityImg}
                        delay={2}
                        source="INEGI, ENSU Trimestral 2024"
                        sourceUrl="https://www.inegi.org.mx/programas/ensu/"
                    />

                    {/* Huachicol */}
                    <BentoItem
                        title="Riesgo Industrial"
                        value="Zona Cero"
                        subtitle="Nodo crítico en la red de 170 tomas clandestinas de la CDMX. Alta vulnerabilidad."
                        icon={Flame}
                        color="text-orange-500"
                        image={industryImg}
                        delay={3}
                        source="PEMEX Logística / Reporte de Tomas Clandestinas"
                        sourceUrl="https://www.pemex.com/acerca/informes_publicaciones/Paginas/tomas-clandestinas.aspx"
                    />

                    {/* Infrastructure */}
                    <BentoItem
                        title="Déficit Inversión"
                        value="50 Años"
                        subtitle="Antigüedad promedio de la red hidráulica primaria."
                        icon={Database}
                        color="text-purple-400"
                        image={deficitImg}
                        delay={4}
                        source="SACMEX / PAOT, Informes de Gestión"
                        sourceUrl="https://www.sacmex.cdmx.gob.mx/"
                    />

                    {/* Mobility */}
                    <BentoItem
                        title="Colapso Movilidad"
                        value="227K"
                        subtitle="Usuarios diarios proyectados en Línea 0 Metrobús. Infraestructura saturada."
                        icon={Train}
                        color="text-cyan-400"
                        image={mobilityImg}
                        delay={5}
                        source="Metrobús CDMX / Estudios de Aforo Línea 0"
                        sourceUrl="https://www.metrobus.cdmx.gob.mx/"
                    />
                </div>
            </div>
        </section>
    );
};

export default CrisisGrid;
